/*
 * Free mystery gift
 *
 * Offer ON:  keeps exactly one gift line (quantity 1, on top) in the cart
 *            whenever the cart holds anything else, and takes it out when the
 *            cart is otherwise empty.
 * Offer OFF: takes out any gift line still sitting in a cart from when the
 *            offer was on.
 * Either way, when a shopper comes back to a tab that has been in the
 * background a while (or returns with the back button), it asks the store
 * whether the offer is still on, fixes the cart, and redraws the cart if it
 * changed, so an old tab never shows an ended offer.
 *
 * Configured from Theme settings > Cart (see snippets/mystery-gift.liquid).
 *
 * Products reach the cart from several places (theme buttons, zerno-pdp.js,
 * the bundle builder, GemPages, apps), so rather than hooking each one this
 * watches every request to /cart/add, /cart/change, /cart/update and
 * /cart/clear and re-checks the cart after it finishes.
 */
(function () {
  var config = window.theme && window.theme.mysteryGift;
  if (!config || window.__zernoMysteryGift) return;
  window.__zernoMysteryGift = true;

  // How long a tab must sit in the background before coming back to it
  // re-checks the offer and redraws the cart.
  var STALE_AFTER_MS = 30 * 1000;

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var nativeFetch = window.fetch.bind(window);
  var running = false;
  var queued = false;
  var queuedReturn = false;
  var timer = null;
  var hiddenAt = 0;

  function root() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function post(path, body) {
    return nativeFetch(root() + path, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(body),
    });
  }

  function getCart() {
    return nativeFetch(root() + 'cart.js', {headers: {Accept: 'application/json'}})
      .then(function (r) { return r.json(); });
  }

  function giftId() { return Number(config.variantId) || 0; }
  function giftProduct() { return Number(config.productId) || 0; }
  function offerOn() { return config.enabled === true && giftId() > 0; }

  // Today's gift, exactly.
  function isCurrentGift(item) {
    return giftId() > 0 && Number(item.variant_id) === giftId();
  }

  // Any line the offer is responsible for: the gift product in any variant,
  // or a line this script tagged when it added it.
  function isGiftLine(item) {
    if (isCurrentGift(item)) return true;
    if (giftProduct() > 0 && Number(item.product_id) === giftProduct()) return true;
    return Boolean(item.properties && item.properties._mystery_gift);
  }

  function addGift() {
    return post('cart/add.js', {
      items: [{id: giftId(), quantity: 1, properties: {_mystery_gift: 'true'}}],
    }).then(function (r) { return r.ok; });
  }

  function removeLines(lines) {
    var updates = {};
    lines.forEach(function (line) { updates[line.key] = 0; });
    return post('cart/update.js', {updates: updates}).then(function (r) { return r.ok; });
  }

  /*
   * Re-reads the offer settings from the store (sections/api-mystery-gift),
   * so a tab opened before the offer was switched off learns that it is off.
   * Keeps the settings it has if the request fails.
   */
  function refreshConfig() {
    return nativeFetch(root() + '?sections=api-mystery-gift', {headers: {Accept: 'application/json'}})
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var html = data && data['api-mystery-gift'];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-mystery-gift-config]');
        if (node) config = JSON.parse(node.textContent);
      })
      .catch(function () {});
  }

  /*
   * Brings the cart in line with the offer. Resolves true when it changed
   * the cart, so the UI knows to redraw.
   */
  function reconcile() {
    return getCart().then(function (cart) {
      var gifts = cart.items.filter(isGiftLine);
      var paidCount = cart.items.length - gifts.length;

      // Offer off, nothing else in the cart, or the gift has sold out:
      // no gift belongs in this cart.
      if (!offerOn() || paidCount === 0 || config.available === false) {
        return gifts.length ? removeLines(gifts) : false;
      }

      if (gifts.length === 0) return addGift();

      // Already right: one line, today's gift, quantity 1, on top.
      if (
        gifts.length === 1 &&
        isCurrentGift(gifts[0]) &&
        gifts[0].quantity === 1 &&
        isCurrentGift(cart.items[0])
      ) {
        return false;
      }

      // Anything else (quantity raised, duplicated, an older gift, or not on
      // top) is cleared and one fresh gift is added. Shopify lists the newest
      // line first, so re-adding also moves it to the top.
      return removeLines(gifts).then(function (ok) {
        if (!ok) return false;
        return addGift().then(function () { return true; });
      });
    });
  }

  function cartPage() {
    return document.querySelector('cart-element[data-cart-page]');
  }

  function cartDrawer() {
    return Array.prototype.find.call(
      document.querySelectorAll('cart-element'),
      function (el) { return el.isCartDrawer; }
    );
  }

  // True when the cart lines drawn inside root do not match the real cart.
  function linesDiffer(root, cart) {
    var rows = root.querySelectorAll('[data-cart-item]');
    if (rows.length !== cart.items.length) return true;
    var shown = {};
    Array.prototype.forEach.call(rows, function (row) {
      var field = row.querySelector('[data-quantity-field]');
      shown[row.getAttribute('data-item')] = field ? Number(field.value) : -1;
    });
    return cart.items.some(function (item) { return shown[item.key] !== item.quantity; });
  }

  // True when the cart on screen (count bubble, drawer or cart page) no longer
  // matches the real cart, e.g. it was changed in another tab meanwhile.
  function screenIsStale(cart) {
    var page = cartPage();
    if (page) return linesDiffer(page, cart);

    var countWrong = Array.prototype.some.call(
      document.querySelectorAll('[data-header-cart-count]'),
      function (el) { return Number(el.textContent.trim() || 0) !== cart.item_count; }
    );
    if (countWrong) return true;

    var drawer = cartDrawer();
    if (!drawer) return false;
    if (drawer.isCartDrawerLoaded) return linesDiffer(drawer, cart);
    // Not opened yet: it will be drawn from the copy saved at page load.
    var saved = drawer.querySelector('[data-cart-drawer-template]');
    return saved ? linesDiffer(saved.content || saved, cart) : false;
  }

  /*
   * Redraws the cart drawer and cart count. The theme opens the drawer at the
   * end of every redraw; a gift fix-up (often on page load, or on coming back
   * to a tab) must not pop the cart open on someone who is just browsing, so a
   * closed drawer is redrawn without opening it.
   */
  function redrawDrawer() {
    var drawer = cartDrawer();
    if (drawer && !drawer.isCartDrawerOpen && typeof drawer.openCartDrawer === 'function') {
      var hadOwn = Object.prototype.hasOwnProperty.call(drawer, 'openCartDrawer');
      var original = drawer.openCartDrawer;
      var restored = false;
      var onRedrawn;
      var restore = function () {
        if (restored) return;
        restored = true;
        document.removeEventListener('theme:cart:added', onRedrawn);
        if (hadOwn) drawer.openCartDrawer = original;
        else delete drawer.openCartDrawer;
      };
      // The theme fires theme:cart:added and then calls openCartDrawer in the
      // same tick, so restore on the next tick.
      onRedrawn = function () { setTimeout(restore, 0); };
      drawer.openCartDrawer = function () {};
      document.addEventListener('theme:cart:added', onRedrawn);
      setTimeout(restore, 8000);
    }
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
  }

  // The cart page is rendered server-side and has no drawer, so it reloads.
  function redraw() {
    if (cartPage()) window.location.reload();
    else redrawDrawer();
  }

  function refreshUI(changed, cameBack) {
    if (changed) {
      redraw();
      return null;
    }
    if (!cameBack) return null;

    // Coming back to the tab with the gift already right: redraw only if the
    // cart was changed somewhere else meanwhile. A redraw sends the theme's
    // "added to cart" signal, which closes the theme's open popups (quick
    // view) and clears a half-filled gift card form, so it is never done for
    // nothing.
    return getCart().then(function (cart) {
      if (screenIsStale(cart)) redraw();
    });
  }

  function sync(cameBack) {
    if (running) {
      queued = true;
      queuedReturn = queuedReturn || Boolean(cameBack);
      return;
    }
    running = true;
    var job = cameBack ? refreshConfig().then(reconcile) : reconcile();
    job
      .then(function (changed) { return refreshUI(changed, cameBack); })
      .catch(function (error) { console.warn('[mystery-gift]', error); })
      .then(function () {
        running = false;
        if (queued) {
          var again = queuedReturn;
          queued = false;
          queuedReturn = false;
          sync(again);
        }
      });
  }

  function scheduleSync() {
    clearTimeout(timer);
    timer = setTimeout(function () { sync(false); }, 250);
  }

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && input.url) return input.url;
    return String(input || '');
  }

  window.fetch = function (input) {
    var request = nativeFetch.apply(window, arguments);
    if (CART_MUTATION.test(urlOf(input).split('#')[0])) {
      request.then(scheduleSync, scheduleSync);
    }
    return request;
  };

  // Some apps still use XHR (jQuery $.post etc.).
  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_MUTATION.test(String(url))) {
      this.addEventListener('loadend', scheduleSync);
    }
    return nativeOpen.apply(this, arguments);
  };

  // Coming back to a tab that sat in the background for a while.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    if (hiddenAt && Date.now() - hiddenAt >= STALE_AFTER_MS) {
      hiddenAt = 0;
      sync(true);
    }
  });

  // Back/forward restores the old page from memory without running anything.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) sync(true);
  });

  // On page load: only when there is something to do. With the offer off and
  // no gift in the cart, this skips a cart request on every page view.
  if (offerOn() || config.cartHasGift) scheduleSync();
})();
